'use client';

import { BackButton } from '@wt/shared/components/BackButton';
import { playlightShowDiscovery } from '@wt/shared/libs/playlight/PlaylightInitialise';
import { getGameIconUrl, getGameInfo } from '@wt/utilities/fetchDataFromCDN';
import { ITeuteufGame, ITeuteufGameInfo } from '@wt/utilities/interfaces';
import { getSiteMode } from '@wt/utilities/siteMode';
import { useEffect, useState } from 'react';

const LANG_CODE = 'en';
const UTM_CAMPAIGN = 'game_links_page';

export const TeuteufGamesContent = () => {
  const [content, setContent] = useState<ITeuteufGameInfo>();

  useEffect(() => {
    (async () => {
      try {
        const data = await getGameInfo();
        setContent(data);
      } catch {
        return;
      }
    })();
  }, []);

  if (!content) return <></>;

  return (
    <div className="flex h-full flex-1 grow flex-col items-center gap-2 px-4 py-2 pb-5 md:gap-5 md:px-8 md:py-5">
      <div className="relative flex w-full max-w-screen-xl items-center justify-center">
        <BackButton />
        <div className="w-full items-center justify-center text-center text-xl font-medium lg:text-4xl">
          {content.heading.translations[LANG_CODE]}
        </div>
      </div>
      <div className="mb-2 flex w-full max-w-xl items-center justify-center text-center text-base lg:text-lg">
        {content.subheading.translations[LANG_CODE]}
      </div>
      <div className="flex flex-col gap-3 md:hidden">
        {content.games.map((g, i) => (
          <MobileGameItem key={i} game={g} langCode={LANG_CODE} />
        ))}
      </div>
      <div className="mt-4 hidden max-w-screen-xl grid-cols-3 gap-x-4 gap-y-8 md:grid lg:grid-cols-4">
        {getSiteMode() == 'whentaken-movies' && (
          <a
            onClick={() => {
              playlightShowDiscovery();
            }}
            target="_blank"
            rel="noreferrer"
            className="relative cursor-pointer flex min-h-[150px] w-full flex-col items-center rounded-2xl bg-base-200 p-4 pt-8 hover:bg-secondary movies:bg-primary-content movies:hover:bg-[#545145]"
          >
            <img
              src={'/assets/images/playlight.png'}
              className="absolute top-0 z-auto mb-4 size-10 -translate-y-1/2"
            />
            <h3 className="mt-2 text-center text-lg font-bold leading-none text-info-content">
              More Games
            </h3>
            <p className="flex h-full items-center justify-center text-center text-sm">
              Discover amazing new games tailored just for you with our partner
              Playlight!
            </p>
          </a>
        )}
        {content.games.map((g, i) => (
          <DesktopGameItem key={i} game={g} langCode={LANG_CODE} />
        ))}
      </div>
    </div>
  );
};

const MobileGameItem = ({
  game,
  langCode,
}: {
  game: ITeuteufGame;
  langCode: string;
}) => {
  return (
    <a
      href={`${game.url}?utm_source=${getSiteMode()}&utm_campaign=${UTM_CAMPAIGN}`}
      target="_blank"
      rel="noreferrer"
      className="flex w-full flex-row items-start gap-3 rounded-2xl bg-base-200 p-4 hover:bg-secondary"
    >
      <img src={getGameIconUrl(game.name)} className="size-10" />
      <div className="flex w-full flex-col">
        <h3 className="text-base font-bold text-info-content">{game.name}</h3>
        <p className="text-sm">{game.captions[langCode]}</p>
      </div>
    </a>
  );
};

const DesktopGameItem = ({
  game,
  langCode,
}: {
  game: ITeuteufGame;
  langCode: string;
}) => {
  return (
    <a
      href={`${game.url}?utm_source=${getSiteMode()}&utm_campaign=${UTM_CAMPAIGN}`}
      target="_blank"
      rel="noreferrer"
      className="relative flex min-h-[150px] w-full flex-col items-center rounded-2xl bg-base-200 p-4 pt-8 hover:bg-secondary movies:bg-primary-content movies:hover:bg-[#545145]"
    >
      <img
        src={getGameIconUrl(game.name)}
        className="absolute top-0 z-auto mb-4 size-10 -translate-y-1/2"
      />
      <h3 className="mt-2 text-center text-lg font-bold leading-none text-info-content">
        {game.name}
      </h3>
      <p className="flex h-full items-center justify-center text-center text-sm">
        {game.captions[langCode]}
      </p>
    </a>
  );
};
